import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import Select, { components, OptionProps, SingleValue, MultiValue, ActionMeta } from 'react-select';

import { client } from '@admin/libraries/apollo';
import { useItemCategoriesQuery } from '@admin/schema';

interface CategoryOption {
  name: string;
  category: {
    id: string;
    name: string;
    synonyms?: string[] | null;
    custom?: boolean;
    // Full category object from GraphQL for Angular service compatibility
    defaults?: Record<string, { length?: number; width?: number; height?: number; num_movers?: number }>;
  };
  sizingSelection?: string | null;
  custom?: boolean;
  virtualCategory?: boolean;
  customName?: boolean;
}

interface CategorySearchInputProps {
  item: {
    category?: {
      id: string | number;
      defaults?: Record<string, { length?: number; width?: number; height?: number; num_movers?: number }>;
    } | null;
    sizing_selection?: string | null;
    sizingSelection?: string | null;
    custom_category_name?: string | null;
    customCategoryName?: string | null;
  };
  onSubmitCallback?: (event: { event: KeyboardEvent }) => void;
  onSelectCallback: (params: { option: CategoryOption | null; model: CategoryOption | null; label: string }) => void;
}

const categorySizingSelectionDisplayName = (value: string, unit?: string | null): string => {
  return unit ? `${value}-${unit}` : value;
};

const selectableSizingSelections = (category: {
  sizingMetric?: {
    enumerations?: string[] | null;
    range?: string | null;
    unit?: string | null;
  } | null;
}): string[] => {
  const unit = category.sizingMetric?.unit;
  if (category.sizingMetric?.range) {
    const range = category.sizingMetric.range.split(',');
    const min = parseInt(range[0]);
    const max = parseInt(range[1]);
    const values: string[] = [];
    for (let value = min; value <= max; value++) {
      values.push(categorySizingSelectionDisplayName(value.toString(), unit));
    }
    return values;
  } else if (category.sizingMetric?.enumerations) {
    return category.sizingMetric.enumerations.map((enumeration) =>
      categorySizingSelectionDisplayName(enumeration, unit),
    );
  }
  return [];
};

const queryMatchesCategoryName = (displayName: string, query: string): boolean => {
  displayName = displayName || '';
  query = query || '';

  const terms = query.toLowerCase().split(' ');
  return terms.every((term) => displayName.toLowerCase().includes(term));
};

const displaySynonyms = (synonyms: string[] | null | undefined, categoryName: string, query: string): boolean => {
  if (queryMatchesCategoryName(categoryName, query)) {
    return false;
  }

  if (!synonyms || synonyms.length === 0) {
    return false;
  }

  return synonyms.some((synonym) => synonym.toLowerCase().indexOf(query.toLowerCase()) !== -1);
};

const Option = (props: OptionProps<{ value: string; label: string; option: CategoryOption }>) => {
  const { data } = props;
  const option = data.option;
  const query = props.selectProps.inputValue || '';

  // Check synonyms against the base category name (not the display name with sizing)
  const showSynonyms =
    option.category.synonyms &&
    displaySynonyms(option.category.synonyms, option.category.name, query) &&
    !option.virtualCategory;

  return (
    <components.Option {...props}>
      <div>
        <span className="typeahead-category-title">{data.label}</span>
        {showSynonyms && (
          <div>
            also known as{' '}
            <strong>
              {option.category.synonyms
                ?.filter((synonym) => synonym.toLowerCase().indexOf(query.toLowerCase()) !== -1)
                .join(' or ')}
            </strong>
          </div>
        )}
      </div>
    </components.Option>
  );
};

export const CategorySearchInput: React.FC<CategorySearchInputProps> = ({
  item,
  onSubmitCallback,
  onSelectCallback,
}) => {
  // Normalize item props to handle both snake_case (Angular) and camelCase (GraphQL) formats
  // Recalculate on every render to avoid stale data when item properties are mutated in place
  const normalizedItem = {
    ...item,
    sizing_selection: item.sizing_selection || item.sizingSelection || null,
    custom_category_name: item.custom_category_name || item.customCategoryName || null,
  };

  const { data: itemCategories, loading: loadingCategories } = useItemCategoriesQuery({
    client,
    fetchPolicy: 'cache-and-network', // Ensure we get fresh data including synonyms
  });

  const [selectedOption, setSelectedOption] = useState<CategoryOption | null>(null);
  const [customCategoryName, setCustomCategoryName] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  // Generate options from categories (similar to ItemCategoryService.generateItemCategoryOptions)
  const options = useMemo(() => {
    if (!itemCategories?.categories) return [];

    const categoryOptions: CategoryOption[] = [];

    itemCategories.categories.forEach((category) => {
      // Filter: show if move_and_pack_visible and not deleted, OR if it's the currently selected category
      // IMPORTANT: Always include custom categories (even if filtered) so we can create the virtual category option
      const shouldShow =
        category.custom || // Always show custom categories
        (category.moveAndPackVisible && !category.deletedAt) ||
        (normalizedItem.category && category.id === normalizedItem.category.id.toString());

      if (!shouldShow) return;

      // Transform GraphQL categoryDefaults into Angular service format
      // Angular expects: defaults[sizingSelection || 'default']
      const defaults: Record<string, { length?: number; width?: number; height?: number; num_movers?: number }> = {};
      if (category.categoryDefaults?.defaults) {
        category.categoryDefaults.defaults.forEach((defaultValue) => {
          const key = defaultValue.sizingSelection || 'default';
          defaults[key] = {
            length: defaultValue.length ?? undefined,
            width: defaultValue.width ?? undefined,
            height: defaultValue.height ?? undefined,
            num_movers: defaultValue.numMovers ?? undefined,
          };
        });
      }

      if (category.sizingMetric) {
        const sizingSelections = selectableSizingSelections(category);
        sizingSelections.forEach((selectableSizingSelection) => {
          const selection = selectableSizingSelection.split('-')[0];
          categoryOptions.push({
            name: `${category.name} (${selectableSizingSelection})`,
            category: {
              id: category.id,
              name: category.name,
              synonyms: category.synonyms ?? null,
              custom: category.custom || false,
              defaults: defaults,
            },
            sizingSelection: selection,
            custom: category.custom || false,
          });
        });
      } else {
        categoryOptions.push({
          name: category.name,
          category: {
            id: category.id,
            name: category.name,
            synonyms: category.synonyms ?? null,
            custom: category.custom || false,
            defaults: defaults,
          },
          sizingSelection: null,
          custom: category.custom || false,
        });
      }
    });

    // Append "virtual category" option - find custom category from ALL categories, not just filtered ones
    // First try to find custom category in filtered options
    let customOption = categoryOptions.find((option) => option.category.custom);

    // If not found in filtered options, find it in ALL categories from GraphQL
    if (!customOption && itemCategories?.categories) {
      const customCategory = itemCategories.categories.find((cat) => cat.custom);

      if (customCategory) {
        // Create a custom option from the GraphQL category
        const defaults: Record<string, { length?: number; width?: number; height?: number; num_movers?: number }> = {};
        if (customCategory.categoryDefaults?.defaults) {
          customCategory.categoryDefaults.defaults.forEach((defaultValue) => {
            const key = defaultValue.sizingSelection || 'default';
            defaults[key] = {
              length: defaultValue.length ?? undefined,
              width: defaultValue.width ?? undefined,
              height: defaultValue.height ?? undefined,
              num_movers: defaultValue.numMovers ?? undefined,
            };
          });
        }

        customOption = {
          name: customCategory.name,
          category: {
            id: customCategory.id,
            name: customCategory.name,
            synonyms: customCategory.synonyms ?? null,
            custom: true,
            defaults: defaults,
          },
          sizingSelection: null,
          custom: true,
        };
      }
    }

    if (customOption) {
      categoryOptions.push({
        name: 'Click here to add a new item',
        category: customOption.category,
        virtualCategory: true,
      });
    } else {
      // FALLBACK: Create virtual category even if no custom category found
      // Use a placeholder category structure - this allows users to create custom categories
      const fallbackCategory = {
        id: 'custom',
        name: 'Other Item',
        synonyms: null,
        custom: true,
        defaults: {},
      };

      categoryOptions.push({
        name: 'Click here to add a new item',
        category: fallbackCategory,
        virtualCategory: true,
      });
    }

    return categoryOptions;
  }, [itemCategories?.categories, normalizedItem.category]);

  // Find initial selected option
  useEffect(() => {
    if (!options.length) return;

    const found = options.find((option) => {
      return (
        option.category.id === normalizedItem.category?.id?.toString() &&
        (!normalizedItem.sizing_selection || option.sizingSelection === normalizedItem.sizing_selection)
      );
    });

    if (found) {
      let optionToSelect = found;
      if (normalizedItem.custom_category_name) {
        // When reopening with a custom category name, mark it as customName
        // and update the name to include the "Custom (name)" format
        optionToSelect = {
          ...found,
          name: `Custom (${normalizedItem.custom_category_name})`,
          customName: true,
        };
      }
      setSelectedOption(optionToSelect);
      // Set customCategoryName so virtual category label displays correctly
      setCustomCategoryName(normalizedItem.custom_category_name || '');
    } else {
      setSelectedOption(null);
      setCustomCategoryName('');
    }
  }, [options, normalizedItem.category?.id, normalizedItem.sizing_selection, normalizedItem.custom_category_name]);

  // Update virtual category option in selectOptions when selected category or input changes
  const selectOptions = useMemo(() => {
    const opts = options.map((option) => {
      // If this is the custom category and we have a custom category name, format the label
      let label = option.name;
      if (option.category.custom && !option.virtualCategory && normalizedItem.custom_category_name) {
        label = `Custom (${normalizedItem.custom_category_name})`;
      }

      return {
        value: `${option.category.id}-${option.sizingSelection || ''}`,
        label: label,
        option: option,
      };
    });

    // Update virtual category label based on what user has typed (like old Angular component)
    const virtualCategoryOpt = opts.find((opt) => opt.option.virtualCategory);
    if (virtualCategoryOpt) {
      // Use searchInput (what user is currently typing) or customCategoryName, otherwise use selected option name
      const displayName = searchInput || customCategoryName || selectedOption?.name || '';
      virtualCategoryOpt.label = displayName
        ? `click here to add new category: ${displayName}`
        : 'Click here to add a new item';
    }

    // Update custom category option label if it has a custom name
    const customOpt = opts.find((opt) => opt.option.custom && !opt.option.virtualCategory);
    if (customOpt && normalizedItem.custom_category_name) {
      customOpt.label = `Custom (${normalizedItem.custom_category_name})`;
    }

    return opts;
  }, [options, selectedOption, customCategoryName, searchInput, normalizedItem.custom_category_name]);

  // Filter options manually and always include virtual category
  const filteredSelectOptions = useMemo(() => {
    // Update labels for custom categories with custom names before filtering
    let optionsToUse = selectOptions;
    if (normalizedItem.custom_category_name) {
      optionsToUse = selectOptions.map((opt) => {
        if (opt.option.category.custom && !opt.option.virtualCategory) {
          return {
            ...opt,
            label: `Custom (${normalizedItem.custom_category_name})`,
          };
        }
        return opt;
      });
    }

    if (!searchInput) {
      // No search - return all options (with updated labels)
      return optionsToUse;
    }

    const query = searchInput.toLowerCase().trim();
    if (!query) {
      return optionsToUse;
    }

    const terms = query.split(/\s+/).filter((t) => t.length > 0);

    // Filter options using our custom logic
    const filtered = optionsToUse.filter((opt) => {
      // Always include virtual category
      if (opt.option.virtualCategory) {
        return true;
      }

      // Use our custom filter logic for other options
      const name = opt.option.name.toLowerCase();
      const baseCategoryName = opt.option.category.name.toLowerCase();
      const synonyms = opt.option.category.synonyms ?? [];
      const synonymStrings = synonyms.length > 0 ? synonyms.map((s) => s.toLowerCase()) : [];
      const allSearchableText = [name, baseCategoryName, ...synonymStrings].join(' ');
      const matches = terms.every((term) => allSearchableText.includes(term));
      return matches;
    });

    return filtered;
  }, [selectOptions, searchInput, normalizedItem.custom_category_name]);

  const selectedValue = useMemo(() => {
    if (!selectedOption) return null;
    // Find in filteredSelectOptions since that's what's passed to react-select
    const found = filteredSelectOptions.find(
      (opt) =>
        opt.option.category.id === selectedOption.category.id &&
        opt.option.sizingSelection === selectedOption.sizingSelection,
    );

    if (!found) return null;

    // The label should already be formatted correctly in filteredSelectOptions
    // but double-check for custom categories
    if (normalizedItem.custom_category_name && found.option.category.custom && !found.option.virtualCategory) {
      return {
        ...found,
        label: `Custom (${normalizedItem.custom_category_name})`,
      };
    }

    return found;
  }, [selectedOption, filteredSelectOptions, normalizedItem.custom_category_name]);

  type SelectOption = { value: string; label: string; option: CategoryOption };

  const handleChange = useCallback(
    (newValue: SingleValue<SelectOption> | MultiValue<SelectOption>, _actionMeta: ActionMeta<SelectOption>) => {
      // Handle single select (not multi-select) - react-select passes null or the selected option
      // Type guard to narrow the type
      if (!newValue || Array.isArray(newValue)) {
        setSelectedOption(null);
        // Clear item properties - update both snake_case and camelCase variants
        if (item.category) item.category = null;
        if (item.sizing_selection !== undefined) item.sizing_selection = null;
        if (item.sizingSelection !== undefined) item.sizingSelection = null;
        if (item.custom_category_name !== undefined) item.custom_category_name = null;
        if (item.customCategoryName !== undefined) item.customCategoryName = null;
        onSelectCallback({ option: null, model: null, label: '' });
        return;
      }

      // At this point, newValue is SingleValue<SelectOption> (not null, not array)
      // Use type assertion since TypeScript doesn't narrow union types with Array.isArray
      const selected = newValue as SelectOption;
      const option = selected.option;
      const customOption = options.find((opt) => opt.custom && !opt.virtualCategory);

      // Update item properties (shared object via ngreact)
      // Pass the full category object with defaults for Angular service compatibility
      // Update both snake_case and camelCase variants for compatibility
      item.category = {
        id: option.category.id,
        defaults: option.category.defaults,
      };
      item.sizing_selection = option.sizingSelection || null;
      item.sizingSelection = option.sizingSelection || null;
      item.custom_category_name = null;
      item.customCategoryName = null;

      if (option.virtualCategory) {
        // Adding new custom category
        if (customOption) {
          customOption.name = customCategoryName || 'Click here to add a new item';
          customOption.customName = true;
          const newOption = { ...customOption };
          item.custom_category_name = customCategoryName || '';
          item.customCategoryName = customCategoryName || '';
          setSelectedOption(newOption);
          const modelForCallback = {
            ...newOption,
            category: {
              ...newOption.category,
              defaults: newOption.category.defaults,
            },
            sizing_selection: newOption.sizingSelection || null,
          };
          onSelectCallback({
            option: newOption,
            model: modelForCallback,
            label: customCategoryName || newOption.name,
          });
        }
      } else if (option.customName) {
        // Adding existing custom category
        if (customOption) {
          item.custom_category_name = customOption.name;
          item.customCategoryName = customOption.name;
          const newOption = { ...option, customName: true };
          setSelectedOption(newOption);
          const modelForCallback = {
            ...newOption,
            category: {
              ...newOption.category,
              defaults: newOption.category.defaults,
            },
            sizing_selection: newOption.sizingSelection || null,
          };
          const displayLabel = customOption.name ? `Custom (${customOption.name})` : 'Custom';
          onSelectCallback({ option: newOption, model: modelForCallback, label: displayLabel });
        }
      } else {
        // Regular category selection
        setSelectedOption(option);
        // Pass model with category that has defaults and sizing_selection property for Angular compatibility
        // Ensure category object is properly structured with defaults
        const modelForCallback = {
          ...option,
          category: {
            ...option.category,
            defaults: option.category.defaults,
          },
          sizing_selection: option.sizingSelection || null,
        };
        const displayLabel = normalizedItem.custom_category_name
          ? `Custom (${normalizedItem.custom_category_name})`
          : option.name;
        onSelectCallback({ option: option, model: modelForCallback, label: displayLabel });
      }
    },
    [options, customCategoryName, normalizedItem, onSelectCallback],
  );

  const handleInputChange = useCallback((inputValue: string) => {
    setSearchInput(inputValue);
    setCustomCategoryName(inputValue);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.keyCode === 13) && onSubmitCallback) {
        onSubmitCallback({ event: e.nativeEvent as unknown as KeyboardEvent });
      }
    },
    [onSubmitCallback],
  );

  if (loadingCategories) {
    return <div>Loading categories...</div>;
  }

  return (
    <Select
      classNamePrefix="Select"
      options={filteredSelectOptions}
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder="Select a category..."
      isSearchable
      isClearable
      filterOption={() => true}
      components={{ Option }}
      // Disable react-select's default filtering since we handle filtering via filteredSelectOptions
      getOptionLabel={(option: any) => {
        const opt = option?.data || option;
        // If this is a custom category with a custom name, format the label
        if (opt?.option?.category?.custom && !opt?.option?.virtualCategory && normalizedItem.custom_category_name) {
          return `Custom (${normalizedItem.custom_category_name})`;
        }
        return opt?.label || '';
      }}
      getOptionValue={(option: any) => {
        const opt = option?.data || option;
        return opt?.value || '';
      }}
      className="category-search-input typeahead-category-container"
      styles={{
        control: (base) => ({
          ...base,
          minHeight: '34px',
        }),
      }}
    />
  );
};
